import React, { useState, useEffect, useContext } from "react";
import {
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Eye,
  Calendar,
  AlertCircle,
  User,
  FileType,
  MessageSquare,
} from "lucide-react";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import AlertModal from "../components/AlertModal";
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";

// Note: Authorization is handled by apiFetch; it returns a Response

const Mo3tiAdminDocumentRequests = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { profile } = useProfile();

  const [pendingRequests, setPendingRequests] = useState([]);
  const [completedRequests, setCompletedRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [comments, setComments] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Duplicate-document confirmation state (for upload requests)
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateExistingDoc, setDuplicateExistingDoc] = useState(null);
  const [pendingApproveId, setPendingApproveId] = useState(null);

  // Admin permissions state
  const [adminLevel, setAdminLevel] = useState(null);
  const [documentFeatures, setDocumentFeatures] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [viewType, setViewType] = useState("");

  // Translations
  const text = {
    en: {
      // Title
      title: "Document Requests Management",
      subtitle: "Manage employee document requests efficiently",

      // Tabs
      pending: "Pending",
      completed: "Completed",
      lastMonth: "Last Month",

      // Empty States
      noPendingRequests: "No Pending Requests",
      noPendingDesc: "All document requests have been processed",
      noCompletedRequests: "No Completed Requests",
      noCompletedDesc: "No documents generated in the last month",

      // Request Details
      employeeName: "Employee Name",
      type: "Type",
      recipient: "Recipient",
      reason: "Reason",
      requestDate: "Request Date",
      requestedDate: "Requested",
      completedDate: "Completed",
      comments: "Comments",
      notAvailable: "N/A",
      unknownEmployee: "Unknown Employee",

      // Buttons
      processRequest: "Process Request",
      download: "Download",
      preview: "Preview",
      approve: "Approve",
      reject: "Reject",
      approveGenerate: "Approve & Generate",
      approveUpload: "Approve Upload",
      cancel: "Cancel",

      // Modal
      processDocumentRequest: "Process Document Request",
      addComments: "Add comments (optional)",
      addCommentsPlaceholder: "Enter your comments here...",

      // Messages
      documentGenerated: "Document generated and sent to employee!",
      processingFailed: "Failed to process request",
      downloadFailed: "Failed to download document",
      requestRejected: "Request rejected successfully",

      // Loading
      loading: "Loading...",
      processing: "Processing...",
    },
    ar: {
      // Title
      title: "إدارة طلبات المستندات",
      subtitle: "إدارة طلبات مستندات الموظفين بكفاءة",

      // Tabs
      pending: "قيد الانتظار",
      completed: "مكتملة",
      lastMonth: "الشهر الماضي",

      // Empty States
      noPendingRequests: "لا توجد طلبات معلقة",
      noPendingDesc: "تم معالجة جميع طلبات المستندات",
      noCompletedRequests: "لا توجد طلبات مكتملة",
      noCompletedDesc: "لم يتم إنشاء مستندات خلال الشهر الماضي",

      // Request Details
      employeeName: "اسم الموظف",
      type: "النوع",
      recipient: "المستلم",
      reason: "السبب",
      requestDate: "تاريخ الطلب",
      requestedDate: "تاريخ الطلب",
      completedDate: "تاريخ الإتمام",
      comments: "التعليقات",
      notAvailable: "غير متوفر",
      unknownEmployee: "موظف غير معروف",

      // Buttons
      processRequest: "معالجة الطلب",
      download: "تنزيل",
      preview: "معاينة",
      approve: "موافقة",
      reject: "رفض",
      approveGenerate: "موافقة وإنشاء",
      approveUpload: "اعتماد المستند المرفوع",
      cancel: "إلغاء",

      // Modal
      processDocumentRequest: "معالجة طلب المستند",
      addComments: "إضافة تعليقات (اختياري)",
      addCommentsPlaceholder: "أدخل تعليقاتك هنا...",

      // Messages
      documentGenerated: "تم إنشاء المستند وإرساله للموظف!",
      processingFailed: "فشلت معالجة الطلب",
      downloadFailed: "فشل تنزيل المستند",
      requestRejected: "تم رفض الطلب بنجاح",

      // Loading
      loading: "جاري التحميل...",
      processing: "جاري المعالجة...",
    },
  };

  // Screen size tracking
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
  });

  // Alert Modal State
  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
    onConfirm: null,
  });

  const showAlert = (title, message, variant = "info", showCancel = false, onConfirm = null) => {
    setAlert({
      open: true,
      title,
      message,
      variant,
      showCancel,
      onConfirm,
    });
  };

  const closeAlert = () => {
    setAlert({ ...alert, open: false });
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Get adminLevel from localStorage
    const storedAdminLevel = localStorage.getItem("adminLevel");
    const storedRoleRaw = profile?.role || localStorage.getItem("role");
    const storedViewType = localStorage.getItem("viewType");
    if (storedRoleRaw) {
      setUserRole(storedRoleRaw.toLowerCase());
    }
    if (storedViewType) {
      setViewType(storedViewType.toLowerCase());
    }
    if (storedAdminLevel) {
      try {
        const parsed = JSON.parse(storedAdminLevel);
        setAdminLevel(parsed);
        setDocumentFeatures(parsed?.features?.documents || []);
      } catch (error) {
        console.error("Error parsing adminLevel:", error);
      }
    }

    fetchPendingRequests();
    fetchCompletedRequests();
  }, []);

  useEffect(() => {
    const target = window.__notificationTarget;
    if (target?.documentId) {
      const allRequests = [...pendingRequests, ...completedRequests];
      if (allRequests.length > 0) {
        const request = allRequests.find(r => r.id === Number(target.documentId));
        if (request) {
          setSelectedRequest(request);
          setShowProcessModal(true);
          window.__notificationTarget = null;
        }
      }
    }
  }, [pendingRequests, completedRequests]);

  const isPrivilegedRole = userRole === "admin" || userRole === "superadmin";
  const shouldEnforceDocumentPermissions = viewType === "admin";
  const canApproveDocuments =
    !shouldEnforceDocumentPermissions ||
    isPrivilegedRole ||
    documentFeatures.includes("approve");
  const canRejectDocuments =
    !shouldEnforceDocumentPermissions ||
    isPrivilegedRole ||
    documentFeatures.includes("reject");
  const canProcessDocuments = canApproveDocuments || canRejectDocuments;

  const fetchPendingRequests = async () => {
    try {
      setIsLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const managerCode = profile?.infinityCode || localStorage.getItem("infinityCode");
      const storedViewType = localStorage.getItem("viewType");
      const userId = profile?.userId || localStorage.getItem("userId");

      let url;
      if (storedViewType === "admin") {
        // Use visibility endpoint for admin users
        url = `${BASE_URL}/api/document-requests/visibility?status=pending`;
      } else {
        // Use regular endpoint for non-admin users
        url = `${BASE_URL}/api/document-requests/all?status=pending&clientName=${encodeURIComponent(
          clientName || ""
        )}&managerCode=${encodeURIComponent(managerCode || "")}`;
      }

      const response = await apiFetch(url, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch pending requests");
      const data = await response.json();
      const requests = storedViewType === "admin" ? (data.data?.requests || []) : (Array.isArray(data) ? data : []);
      setPendingRequests(requests);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompletedRequests = async () => {
    try {
      setIsLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const managerCode = profile?.infinityCode || localStorage.getItem("infinityCode");
      const storedViewType = localStorage.getItem("viewType");
      const userId = profile?.userId || localStorage.getItem("userId");

      let url;
      if (storedViewType === "admin") {
        // Use visibility endpoint for admin users
        url = `${BASE_URL}/api/document-requests/visibility?status=completed`;
      } else {
        // Use regular endpoint for non-admin users
        url = `${BASE_URL}/api/document-requests/all?status=completed&clientName=${encodeURIComponent(
          clientName || ""
        )}&managerCode=${encodeURIComponent(managerCode || "")}`;
      }

      const response = await apiFetch(url, { method: "GET" });
      if (!response.ok) throw new Error("Failed to fetch completed requests");
      const data = await response.json();
      const requests = storedViewType === "admin" ? (data.data?.requests || []) : (Array.isArray(data) ? data : []);
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

      const recentCompleted = requests.filter((req) => {
        const completedDate = new Date(req.processedDate || req.created_at);
        return completedDate >= oneMonthAgo;
      });

      setCompletedRequests(recentCompleted);
    } catch (error) {
      console.error("Failed to fetch completed requests:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      if (!canApproveDocuments) {
        showAlert("✗ Error", "You do not have permission to approve document requests", "error");
        return;
      }

      // For upload-type requests, check if a document of the same type already exists
      const req = pendingRequests.find((r) => r.id === requestId) || selectedRequest;
      if (req?.requestType?.startsWith("upload:")) {
        const docType = req.requestType.replace("upload:", "");
        try {
          const checkRes = await apiFetch(`${BASE_URL}/api/documents/check/${req.infinityCode}/${docType}`);
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            if (checkData.exists) {
              setDuplicateExistingDoc(checkData.document);
              setPendingApproveId(requestId);
              setShowDuplicateConfirm(true);
              return; // pause for confirmation
            }
          }
        } catch (_) { /* non-blocking */ }
      }

      await doApprove(requestId);
    } catch (error) {
      console.error("Approval failed:", error);
      showAlert("✗ Error", text[language].processingFailed, "error");
    }
  };

  const doApprove = async (requestId) => {
    try {
      setIsLoading(true);
      const adminCode = profile?.infinityCode || localStorage.getItem("infinityCode");
      const res = await apiFetch(`${BASE_URL}/api/document-requests/${requestId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "approved", processedBy: adminCode, comments }),
      });
      if (!res.ok) throw new Error("Approval failed");

      showAlert("✓ Success", text[language].documentGenerated, "success");
      fetchPendingRequests();
      fetchCompletedRequests();
      setShowProcessModal(false);
      setShowDuplicateConfirm(false);
      setDuplicateExistingDoc(null);
      setPendingApproveId(null);
      setComments("");
    } catch (error) {
      console.error("Approval failed:", error);
      showAlert("✗ Error", text[language].processingFailed, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    try {
      // Check if user has reject permission
      if (!canRejectDocuments) {
        showAlert("✗ Error", "You do not have permission to reject document requests", "error");
        return;
      }

      setIsLoading(true);
      const adminCode = profile?.infinityCode || localStorage.getItem("infinityCode");
      const res = await apiFetch(`${BASE_URL}/api/document-requests/${requestId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "rejected", processedBy: adminCode, comments }),
      });
      if (!res.ok) throw new Error("Rejection failed");

      showAlert("✓ Success", text[language].requestRejected, "success");
      fetchPendingRequests();
      setShowProcessModal(false);
      setComments("");
    } catch (error) {
      console.error("Rejection failed:", error);
      showAlert("✗ Error", text[language].processingFailed, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Fixed handleDownload function with auth headers
  const handleDownload = async (documentId) => {
    try {
      const response = await apiFetch(`${BASE_URL}/api/documents/download/${documentId}`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "document.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Ensure .pdf extension if not present
      if (!filename.toLowerCase().endsWith(".pdf")) {
        filename += ".pdf";
      }

      // Convert response to blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.setAttribute("download", filename); // Force download attribute
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      showAlert("✗ Error", text[language].downloadFailed, "error");
    }
  };

  // handlePreview: direct URL open with token query param (avoids popup blocking)
  const handlePreview = (documentId) => {
    const token = localStorage.getItem("token");
    const url = `${BASE_URL}/api/documents/preview/${documentId}?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const getResponsiveStyles = () => {
    const { isMobile, isTablet, isDesktop } = screenSize;
    const isRTL = language === "ar";

    return {
      container: {
        marginTop: "-40px",
        padding: isMobile ? "0 12px" : "0",
        direction: isRTL ? "rtl" : "ltr",
      },

      card: {
        background:
          theme === "light"
            ? "rgba(255, 255, 255, 1)"
            : "rgba(255, 255, 255, 0.01)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border:
          theme === "light"
            ? "1px solid rgba(0, 0, 0, 0.05)"
            : "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: isMobile ? "12px" : "16px",
        padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
        marginBottom: "24px",
        marginLeft: isRTL ? 0 : isMobile ? 0 : 20,
        marginRight: isRTL ? (isMobile ? 0 : 20) : 0,
        boxShadow:
          theme === "light"
            ? "0 8px 32px rgba(0, 0, 0, 0.3)"
            : "0 8px 32px rgba(0, 0, 0, 0.3)",
        transition: "all 0.3s ease",
      },

      header: {
        marginBottom: isMobile ? "20px" : "24px",
      },

      title: {
        fontSize: isMobile ? "20px" : isTablet ? "22px" : "24px",
        fontWeight: "700",
        color: theme === "light" ? "#0f172a" : "#ffffff",
        margin: "0 0 8px 0",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        textAlign: isRTL ? "right" : "left",
        flexDirection: isRTL ? "row" : "row",
      },

      subtitle: {
        fontSize: isMobile ? "13px" : "14px",
        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)",
        margin: 0,
        textAlign: isRTL ? "right" : "left",
      },

      tabContainer: {
        display: "flex",
        gap: isMobile ? "8px" : "12px",
        marginBottom: isMobile ? "20px" : "24px",
        borderBottom:
          theme === "light"
            ? "2px solid #e2e8f0"
            : "2px solid rgba(255, 255, 255, 0.1)",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        flexDirection: isRTL ? "row" : "row",
        paddingBottom: "2px",
      },

      tab: (isActive) => ({
        padding: isMobile ? "10px 16px" : "12px 24px",
        background: isActive
          ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
          : "transparent",
        color: isActive
          ? "white"
          : theme === "light"
            ? "#64748b"
            : "rgba(255, 255, 255, 0.7)",
        border: "none",
        borderRadius: "10px 10px 0 0",
        fontSize: isMobile ? "13px" : "14px",
        fontWeight: "600",
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "6px" : "8px",
        whiteSpace: "nowrap",
        flexDirection: isRTL ? "row-reverse" : "row",
        outline: "none",
      }),

      requestCard: {
        padding: isMobile ? "16px" : "20px",
        border:
          theme === "light"
            ? "2px solid #e2e8f0"
            : "2px solid rgba(255, 255, 255, 0.15)",
        borderRadius: isMobile ? "10px" : "12px",
        marginBottom: isMobile ? "12px" : "16px",
        background: theme === "light" ? "#ffffff" : "rgba(255, 255, 255, 0.02)",
        transition: "all 0.3s ease",
        cursor: "pointer",
      },

      requestHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        flexWrap: "wrap",
        gap: isMobile ? "12px" : "16px",
        flexDirection:
          isMobile && isRTL ? "column-reverse" : isRTL ? "row" : "row",
      },

      requestInfo: {
        flex: 1,
        minWidth: isMobile ? "100%" : "250px",
        textAlign: isRTL ? "left" : "left",
      },

      employeeName: {
        fontSize: isMobile ? "16px" : "18px",
        fontWeight: "600",
        color: theme === "light" ? "#0f172a" : "#ffffff",
        margin: "0 0 12px 0",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexDirection: isRTL ? "row" : "row",
      },

      requestDetail: {
        fontSize: isMobile ? "13px" : "14px",
        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.7)",
        margin: "6px 0",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexDirection: isRTL ? "row" : "row",
        lineHeight: "1.5",
      },

      detailLabel: {
        fontWeight: "600",
        color: theme === "light" ? "#334155" : "rgba(255, 255, 255, 0.9)",
        minWidth: isMobile ? "70px" : "80px",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        flexDirection: isRTL ? "row" : "row",
      },

      button: {
        background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
        color: "white",
        border: "none",
        borderRadius: isMobile ? "8px" : "10px",
        padding: isMobile ? "10px 20px" : "12px 24px",
        fontSize: isMobile ? "13px" : "14px",
        fontWeight: "600",
        cursor: isLoading ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        alignItems: "center",
        gap: isMobile ? "6px" : "8px",
        whiteSpace: "nowrap",
        opacity: isLoading ? 0.7 : 1,
        outline: "none",
        flexDirection: isRTL ? "row-reverse" : "row",
        width: isMobile ? "100%" : "auto",
        justifyContent: "center",
      },

      emptyState: {
        textAlign: "center",
        padding: isMobile ? "40px 20px" : "60px 20px",
        color: theme === "light" ? "#64748b" : "rgba(255, 255, 255, 0.6)",
      },

      emptyStateTitle: {
        fontSize: isMobile ? "18px" : "20px",
        fontWeight: "600",
        marginBottom: "8px",
        marginTop: "16px",
        color: theme === "light" ? "#374151" : "rgba(255, 255, 255, 0.8)",
      },

      emptyStateDesc: {
        fontSize: isMobile ? "13px" : "14px",
        margin: 0,
      },

      modal: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: isMobile ? "16px" : "20px",
        direction: isRTL ? "rtl" : "ltr",
      },

      modalContent: {
        background:
          theme === "light"
            ? "rgba(255, 255, 255, 0.98)"
            : "rgba(30, 41, 59, 0.98)",
        padding: isMobile ? "24px" : "32px",
        borderRadius: isMobile ? "16px" : "20px",
        width: "100%",
        maxWidth: isMobile ? "100%" : "500px",
        maxHeight: "85vh",
        overflowY: "auto",
        boxShadow:
          theme === "light"
            ? "0 20px 60px rgba(0, 0, 0, 0.2)"
            : "0 20px 60px rgba(0, 0, 0, 0.6)",
        color: theme === "light" ? "#0f172a" : "#ffffff",
        backdropFilter: "blur(20px)",
        border:
          theme === "light"
            ? "1px solid rgba(0, 0, 0, 0.1)"
            : "1px solid rgba(255, 255, 255, 0.1)",
      },

      modalTitle: {
        fontSize: isMobile ? "18px" : "20px",
        fontWeight: "700",
        marginBottom: isMobile ? "20px" : "24px",
        color: theme === "light" ? "#0f172a" : "#ffffff",
        textAlign: isRTL ? "right" : "left",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexDirection: isRTL ? "row-reverse" : "row",
      },

      formLabel: {
        display: "block",
        fontSize: isMobile ? "13px" : "14px",
        fontWeight: "600",
        color: theme === "light" ? "#334155" : "rgba(255, 255, 255, 0.9)",
        marginBottom: "8px",
        textAlign: isRTL ? "right" : "left",
      },

      textarea: {
        width: "100%",
        minHeight: isMobile ? "100px" : "120px",
        padding: isMobile ? "10px" : "12px",
        marginBottom: isMobile ? "20px" : "24px",
        border:
          theme === "light"
            ? "2px solid #e2e8f0"
            : "2px solid rgba(255, 255, 255, 0.2)",
        borderRadius: isMobile ? "10px" : "12px",
        background: theme === "light" ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
        color: theme === "light" ? "#0f172a" : "#ffffff",
        fontSize: isMobile ? "13px" : "14px",
        fontFamily: "inherit",
        resize: "vertical",
        outline: "none",
        transition: "border-color 0.3s ease",
        boxSizing: "border-box",
        textAlign: isRTL ? "right" : "left",
        direction: isRTL ? "rtl" : "ltr",
      },

      buttonGroup: {
        display: "flex",
        gap: isMobile ? "10px" : "12px",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        flexDirection: isRTL ? "row-reverse" : "row",
      },

      secondaryButton: {
        background:
          theme === "light"
            ? "rgba(0, 0, 0, 0.05)"
            : "rgba(255, 255, 255, 0.1)",
        color: theme === "light" ? "#374151" : "#ffffff",
        border: "none",
        borderRadius: isMobile ? "8px" : "10px",
        padding: isMobile ? "10px 20px" : "12px 24px",
        fontSize: isMobile ? "13px" : "14px",
        fontWeight: "600",
        cursor: isLoading ? "not-allowed" : "pointer",
        transition: "all 0.3s ease",
        opacity: isLoading ? 0.7 : 1,
        outline: "none",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexDirection: isRTL ? "row-reverse" : "row",
        flex: isMobile ? "1" : "0",
        justifyContent: "center",
      },

      actionButtons: {
        display: "flex",
        gap: "8px",
        flexDirection: isMobile ? "column" : "column",
        width: isMobile ? "100%" : "auto",
      },

      downloadButton: {
        background: "linear-gradient(135deg, #10b981, #059669)",
        color: "white",
        border: "none",
        borderRadius: isMobile ? "8px" : "8px",
        padding: isMobile ? "10px 16px" : "8px 16px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        transition: "all 0.3s ease",
        outline: "none",
        flexDirection: isRTL ? "row-reverse" : "row",
        justifyContent: "center",
        width: isMobile ? "100%" : "auto",
      },

      previewButton: {
        background:
          theme === "light"
            ? "rgba(0, 0, 0, 0.05)"
            : "rgba(255, 255, 255, 0.1)",
        color: theme === "light" ? "#374151" : "#ffffff",
        border: "none",
        borderRadius: isMobile ? "8px" : "8px",
        padding: isMobile ? "10px 16px" : "8px 16px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        transition: "all 0.3s ease",
        outline: "none",
        flexDirection: isRTL ? "row-reverse" : "row",
        justifyContent: "center",
        width: isMobile ? "100%" : "auto",
      },

      badge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
        background:
          theme === "light"
            ? "rgba(59, 130, 246, 0.1)"
            : "rgba(59, 130, 246, 0.2)",
        color: "#3b82f6",
        flexDirection: isRTL ? "row-reverse" : "row",
      },
    };
  };

  const styles = getResponsiveStyles();

  const handleCardHover = (e, isEntering) => {
    if (screenSize.isMobile) return;

    if (isEntering) {
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow =
        theme === "light"
          ? "0 12px 40px rgba(0, 0, 0, 0.12)"
          : "0 12px 40px rgba(0, 0, 0, 0.4)";
    } else {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow =
        theme === "light"
          ? "0 8px 32px rgba(0, 0, 0, 0.08)"
          : "0 8px 32px rgba(0, 0, 0, 0.3)";
    }
  };

  return (
    <div style={styles.container}>
      <div
        style={styles.card}
      // onMouseEnter={(e) => handleCardHover(e, true)}
      // onMouseLeave={(e) => handleCardHover(e, false)}
      >
        <div style={styles.header}>
          <h2 style={styles.title}>
            <FileText size={screenSize.isMobile ? 20 : 24} />
            {text[language].title}
          </h2>
          <p style={styles.subtitle}>{text[language].subtitle}</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabContainer}>
          <button
            style={styles.tab(activeTab === "pending")}
            onClick={() => setActiveTab("pending")}
          // onMouseEnter={(e) => {
          //   if (activeTab !== 'pending' && !screenSize.isMobile) {
          //     e.currentTarget.style.background = theme === "light"
          //       ? "rgba(0, 0, 0, 0.05)"
          //       : "rgba(255, 255, 255, 0.05)";
          //   }
          // }}
          // onMouseLeave={(e) => {
          //   if (activeTab !== 'pending') {
          //     e.currentTarget.style.background = "transparent";
          //   }
          // }}
          >
            <Clock size={16} />
            {text[language].pending} ({pendingRequests.length})
          </button>
          <button
            style={styles.tab(activeTab === "completed")}
            onClick={() => setActiveTab("completed")}
          // onMouseEnter={(e) => {
          //   if (activeTab !== 'completed' && !screenSize.isMobile) {
          //     e.currentTarget.style.background = theme === "light"
          //       ? "rgba(0, 0, 0, 0.05)"
          //       : "rgba(255, 255, 255, 0.05)";
          //   }
          // }}
          // onMouseLeave={(e) => {
          //   if (activeTab !== 'completed') {
          //     e.currentTarget.style.background = "transparent";
          //   }
          // }}
          >
            <CheckCircle size={16} />
            {text[language].completed} - {text[language].lastMonth} (
            {completedRequests.length})
          </button>
        </div>

        {/* Pending Requests Tab */}
        {activeTab === "pending" && (
          <>
            {isLoading ? (
              <div style={styles.emptyState}>
                <Clock
                  size={screenSize.isMobile ? 48 : 64}
                  style={{ marginBottom: "20px", opacity: 0.3 }}
                />
                <h3 style={styles.emptyStateTitle}>{text[language].loading}</h3>
              </div>
            ) : pendingRequests.length === 0 ? (
              <div style={styles.emptyState}>
                <Clock
                  size={screenSize.isMobile ? 48 : 64}
                  style={{ marginBottom: "20px", opacity: 0.3 }}
                />
                <h3 style={styles.emptyStateTitle}>
                  {text[language].noPendingRequests}
                </h3>
                <p style={styles.emptyStateDesc}>
                  {text[language].noPendingDesc}
                </p>
              </div>
            ) : (
              pendingRequests.map((request) => (
                <div
                  key={request.id}
                  style={styles.requestCard}
                // onMouseEnter={(e) => {
                //   if (!screenSize.isMobile) {
                //     e.currentTarget.style.transform = "scale(1.01)";
                //     e.currentTarget.style.borderColor = "#2e7cba";
                //   }
                // }}
                // onMouseLeave={(e) => {
                //   if (!screenSize.isMobile) {
                //     e.currentTarget.style.transform = "scale(1)";
                //     e.currentTarget.style.borderColor = theme === "light"
                //       ? "#e2e8f0"
                //       : "rgba(255, 255, 255, 0.15)";
                //   }
                // }}
                >
                  <div style={styles.requestHeader}>
                    <div style={styles.requestInfo}>
                      <h4 style={styles.employeeName}>
                        <User size={screenSize.isMobile ? 16 : 18} />
                        {request.employee?.sr || text[language].unknownEmployee}
                      </h4>

                      <p style={styles.requestDetail}>
                        <span style={styles.detailLabel}>
                          <FileType size={14} />
                          {text[language].type}:
                        </span>
                        <span style={styles.badge}>
                          {request.requestType?.startsWith('upload:')
                            ? `📤 ${request.requestType.replace('upload:', '')}`
                            : request.requestType}
                        </span>
                      </p>

                      {request.recipient && !request.requestType?.startsWith('upload:') && (
                        <p style={styles.requestDetail}>
                          <span style={styles.detailLabel}>
                            <User size={14} />
                            {text[language].recipient}:
                          </span>
                          {request.recipient}
                        </p>
                      )}

                      <p style={styles.requestDetail}>
                        <span style={styles.detailLabel}>
                          <MessageSquare size={14} />
                          {text[language].reason}:
                        </span>
                        {request.reason || text[language].notAvailable}
                      </p>

                      <p style={styles.requestDetail}>
                        <span style={styles.detailLabel}>
                          <Calendar size={14} />
                          {text[language].requestDate}:
                        </span>
                        {new Date(
                          request.requestedDate || request.created_at
                        ).toLocaleDateString(
                          language === "ar" ? "ar-EG" : "en-US"
                        )}
                      </p>
                    </div>

                    {canProcessDocuments && (
                      <button
                        style={styles.button}
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowProcessModal(true);
                        }}
                        disabled={isLoading}
                      // onMouseEnter={(e) => {
                      //   if (!isLoading && !screenSize.isMobile) {
                      //     e.currentTarget.style.transform = "translateY(-2px)";
                      //     e.currentTarget.style.boxShadow = "0 8px 20px rgba(59, 130, 246, 0.4)";
                      //   }
                      // }}
                      // onMouseLeave={(e) => {
                      //   if (!isLoading) {
                      //     e.currentTarget.style.transform = "translateY(0)";
                      //     e.currentTarget.style.boxShadow = "none";
                      //   }
                      // }}
                      >
                        <FileText size={screenSize.isMobile ? 14 : 16} />
                        {text[language].processRequest}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* Completed Requests Tab */}
        {activeTab === "completed" && (
          <>
            {isLoading ? (
              <div style={styles.emptyState}>
                <CheckCircle
                  size={screenSize.isMobile ? 48 : 64}
                  style={{ marginBottom: "20px", opacity: 0.3 }}
                />
                <h3 style={styles.emptyStateTitle}>{text[language].loading}</h3>
              </div>
            ) : completedRequests.length === 0 ? (
              <div style={styles.emptyState}>
                <CheckCircle
                  size={screenSize.isMobile ? 48 : 64}
                  style={{ marginBottom: "20px", opacity: 0.3 }}
                />
                <h3 style={styles.emptyStateTitle}>
                  {text[language].noCompletedRequests}
                </h3>
                <p style={styles.emptyStateDesc}>
                  {text[language].noCompletedDesc}
                </p>
              </div>
            ) : (
              completedRequests.map((request) => (
                <div
                  key={request.id}
                  style={styles.requestCard}
                // onMouseEnter={(e) => {
                //   if (!screenSize.isMobile) {
                //     e.currentTarget.style.transform = "scale(1.01)";
                //     e.currentTarget.style.borderColor = "#10b981";
                //   }
                // }}
                // onMouseLeave={(e) => {
                //   if (!screenSize.isMobile) {
                //     e.currentTarget.style.transform = "scale(1)";
                //     e.currentTarget.style.borderColor = theme === "light"
                //       ? "#e2e8f0"
                //       : "rgba(255, 255, 255, 0.15)";
                //   }
                // }}
                >
                  <div style={styles.requestHeader}>
                    <div style={styles.requestInfo}>
                      <h4 style={styles.employeeName}>
                        <User size={screenSize.isMobile ? 16 : 18} />
                        {request.employee?.sr || text[language].unknownEmployee}
                      </h4>

                      <p style={styles.requestDetail}>
                        <span style={styles.detailLabel}>
                          <FileType size={14} />
                          {text[language].type}:
                        </span>
                        <span style={styles.badge}>
                          {request.requestType?.startsWith('upload:')
                            ? `📤 ${request.requestType.replace('upload:', '')}`
                            : request.requestType}
                        </span>
                      </p>

                      {request.recipient && !request.requestType?.startsWith('upload:') && (
                        <p style={styles.requestDetail}>
                          <span style={styles.detailLabel}>
                            <User size={14} />
                            {text[language].recipient}:
                          </span>
                          {request.recipient}
                        </p>
                      )}

                      <p style={styles.requestDetail}>
                        <span style={styles.detailLabel}>
                          <Calendar size={14} />
                          {text[language].requestedDate}:
                        </span>
                        {new Date(
                          request.requestedDate || request.created_at
                        ).toLocaleDateString(
                          language === "ar" ? "ar-EG" : "en-US"
                        )}
                      </p>

                      <p style={styles.requestDetail}>
                        <span style={styles.detailLabel}>
                          <CheckCircle size={14} />
                          {text[language].completedDate}:
                        </span>
                        {new Date(request.processedDate).toLocaleDateString(
                          language === "ar" ? "ar-EG" : "en-US"
                        )}
                      </p>

                      {request.comments && (
                        <p style={styles.requestDetail}>
                          <span style={styles.detailLabel}>
                            <MessageSquare size={14} />
                            {text[language].comments}:
                          </span>
                          {request.comments}
                        </p>
                      )}
                    </div>

                    <div style={styles.actionButtons}>
                      <button
                        style={styles.downloadButton}
                        onClick={() => handleDownload(request.generatedFileId)}
                      // onMouseEnter={(e) => {
                      //   if (!screenSize.isMobile) {
                      //     e.currentTarget.style.transform = "translateY(-2px)";
                      //     e.currentTarget.style.boxShadow = "0 6px 16px rgba(16, 185, 129, 0.4)";
                      //   }
                      // }}
                      // onMouseLeave={(e) => {
                      //   e.currentTarget.style.transform = "translateY(0)";
                      //   e.currentTarget.style.boxShadow = "none";
                      // }}
                      >
                        <Download size={16} />
                        {text[language].download}
                      </button>
                      <button
                        style={styles.previewButton}
                        onClick={() => handlePreview(request.generatedFileId)}
                      // onMouseEnter={(e) => {
                      //   e.currentTarget.style.background = theme === "light"
                      //     ? "rgba(0, 0, 0, 0.1)"
                      //     : "rgba(255, 255, 255, 0.15)";
                      // }}
                      // onMouseLeave={(e) => {
                      //   e.currentTarget.style.background = theme === "light"
                      //     ? "rgba(0, 0, 0, 0.05)"
                      //     : "rgba(255, 255, 255, 0.1)";
                      // }}
                      >
                        <Eye size={16} />
                        {text[language].preview}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showProcessModal && (
        <div
          style={styles.modal}
          onClick={() => !isLoading && setShowProcessModal(false)}
        >
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              <FileText size={screenSize.isMobile ? 18 : 20} />
              {text[language].processDocumentRequest}
            </h3>

            <label style={styles.formLabel}>{text[language].addComments}</label>
            <textarea
              placeholder={text[language].addCommentsPlaceholder}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              style={styles.textarea}
              disabled={isLoading}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#2e7cba";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor =
                  theme === "light" ? "#e2e8f0" : "rgba(255, 255, 255, 0.2)";
              }}
            />

            <div style={styles.buttonGroup}>
              {canRejectDocuments && (
                <button
                  style={styles.secondaryButton}
                  onClick={() => handleReject(selectedRequest?.id)}
                  disabled={isLoading}
                // onMouseEnter={(e) => {
                //   if (!isLoading && !screenSize.isMobile) {
                //     e.currentTarget.style.background = theme === "light"
                //       ? "rgba(0, 0, 0, 0.1)"
                //       : "rgba(255, 255, 255, 0.15)";
                //   }
                // }}
                // onMouseLeave={(e) => {
                //   if (!isLoading) {
                //     e.currentTarget.style.background = theme === "light"
                //       ? "rgba(0, 0, 0, 0.05)"
                //       : "rgba(255, 255, 255, 0.1)";
                //   }
                // }}
                >
                  <XCircle size={16} />
                  {text[language].reject}
                </button>
              )}
              {canApproveDocuments && (
                <button
                  style={styles.button}
                  onClick={() => handleApprove(selectedRequest?.id)}
                  disabled={isLoading}
                // onMouseEnter={(e) => {
                //   if (!isLoading && !screenSize.isMobile) {
                //     e.currentTarget.style.transform = "translateY(-2px)";
                //     e.currentTarget.style.boxShadow = "0 8px 20px rgba(59, 130, 246, 0.4)";
                //   }
                // }}
                // onMouseLeave={(e) => {
                //   if (!isLoading) {
                //     e.currentTarget.style.transform = "translateY(0)";
                //     e.currentTarget.style.boxShadow = "none";
                //   }
                // }}
                >
                  <CheckCircle size={16} />
                  {isLoading
                    ? text[language].processing
                    : selectedRequest?.requestType?.startsWith('upload:')
                      ? text[language].approveUpload
                      : text[language].approveGenerate}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Duplicate document confirmation modal ─────────────────────────── */}
      {showDuplicateConfirm && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(4px)", zIndex: 12000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
          onClick={() => { setShowDuplicateConfirm(false); setDuplicateExistingDoc(null); setPendingApproveId(null); }}
        >
          <div
            style={{
              background: theme === "dark"
                ? "linear-gradient(135deg,#0f172a 0%,#1a2a3a 100%)"
                : "linear-gradient(135deg,#fff 0%,#f8fafc 100%)",
              border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid rgba(148,163,184,0.2)",
              borderRadius: 16, padding: "28px 28px 24px",
              width: "100%", maxWidth: 520,
              color: theme === "dark" ? "#e2e8f0" : "#0f172a",
              boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: "rgba(245,158,11,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <AlertCircle size={20} color="#f59e0b" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
                  {language === "ar" ? "مستند موجود بالفعل" : "Existing Document Conflict"}
                </h3>
                <p style={{ margin: "3px 0 0", fontSize: 12, color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                  {language === "ar"
                    ? "يمتلك هذا الموظف بالفعل مستنداً من هذا النوع. هل تريد الموافقة على استبداله؟"
                    : "This employee already has a document of this type. Approving will replace it."}
                </p>
              </div>
            </div>

            {/* Existing doc info */}
            <div style={{
              borderRadius: 10, padding: "14px 16px", marginBottom: 12,
              background: theme === "dark" ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)",
              border: "1px solid rgba(239,68,68,0.2)",
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{
                  display: "inline-block", padding: "2px 10px", borderRadius: 20,
                  fontSize: 11, fontWeight: 700, background: "rgba(239,68,68,0.15)",
                  color: "#ef4444", textTransform: "uppercase",
                }}>
                  {language === "ar" ? "الحالي" : "Current"}
                </span>
                {duplicateExistingDoc?.id && (
                  <button
                    onClick={() => handlePreview(duplicateExistingDoc.id)}
                    style={{
                      background: "transparent", border: "none", cursor: "pointer",
                      color: "#ef4444", display: "flex", alignItems: "center",
                      gap: 4, fontSize: 12, fontWeight: 600,
                    }}
                  >
                    <Eye size={13} />
                    {language === "ar" ? "معاينة" : "Preview"}
                  </button>
                )}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{duplicateExistingDoc?.type}</span>
              {duplicateExistingDoc?.created_at && (
                <span style={{ fontSize: 11, color: theme === "dark" ? "#64748b" : "#94a3b8" }}>
                  {language === "ar" ? "تم الرفع:" : "Uploaded:"}{" "}
                  {new Date(duplicateExistingDoc.created_at).toLocaleDateString(
                    language === "ar" ? "ar-EG" : "en-US"
                  )}
                </span>
              )}
            </div>

            {/* New upload info */}
            <div style={{
              borderRadius: 10, padding: "14px 16px", marginBottom: 20,
              background: theme === "dark" ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.05)",
              border: "1px solid rgba(59,130,246,0.2)",
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              <span style={{
                display: "inline-block", padding: "2px 10px", borderRadius: 20,
                fontSize: 11, fontWeight: 700, background: "rgba(59,130,246,0.15)",
                color: "#3b82f6", textTransform: "uppercase",
              }}>
                {language === "ar" ? "جديد (معلق)" : "New (Pending)"}
              </span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {selectedRequest?.requestType?.replace("upload:", "")}
              </span>
              {selectedRequest?.requestedDate && (
                <span style={{ fontSize: 11, color: theme === "dark" ? "#64748b" : "#94a3b8" }}>
                  {language === "ar" ? "طُلب في:" : "Requested:"}{" "}
                  {new Date(selectedRequest.requestedDate).toLocaleDateString(
                    language === "ar" ? "ar-EG" : "en-US"
                  )}
                </span>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => { setShowDuplicateConfirm(false); setDuplicateExistingDoc(null); setPendingApproveId(null); }}
                style={{
                  flex: 1, padding: "11px 0", borderRadius: 10, cursor: "pointer",
                  background: theme === "dark" ? "rgba(30,41,59,0.8)" : "rgba(241,245,249,0.9)",
                  color: theme === "dark" ? "#cbd5e1" : "#475569",
                  border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
                  fontWeight: 700, fontSize: 14,
                }}
              >
                {language === "ar" ? "إلغاء" : "Cancel"}
              </button>
              <button
                disabled={isLoading}
                onClick={() => doApprove(pendingApproveId)}
                style={{
                  flex: 1, padding: "11px 0", borderRadius: 10, cursor: isLoading ? "not-allowed" : "pointer",
                  background: "linear-gradient(135deg,#2e7cba,#1d4ed8)", color: "#fff",
                  border: "none", fontWeight: 700, fontSize: 14,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  opacity: isLoading ? 0.7 : 1,
                }}
              >
                <CheckCircle size={14} />
                {isLoading
                  ? (language === "ar" ? "جارٍ المعالجة..." : "Processing...")
                  : (language === "ar" ? "موافقة واستبدال" : "Approve & Replace")}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          ::-webkit-scrollbar {
            width: ${screenSize.isMobile ? "4px" : "6px"};
            height: ${screenSize.isMobile ? "4px" : "6px"};
          }
          
          ::-webkit-scrollbar-track {
            background: ${theme === "light"
            ? "rgba(0, 0, 0, 0.05)"
            : "rgba(255, 255, 255, 0.05)"
          };
            border-radius: 3px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: ${theme === "light"
            ? "rgba(0, 0, 0, 0.2)"
            : "rgba(255, 255, 255, 0.2)"
          };
            border-radius: 3px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: ${theme === "light"
            ? "rgba(0, 0, 0, 0.3)"
            : "rgba(255, 255, 255, 0.3)"
          };
          }

          * {
            -webkit-tap-highlight-color: transparent;
          }

          @media (max-width: 768px) {
            * {
              text-rendering: optimizeSpeed;
            }
          }

          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            * {
              text-rendering: optimizeLegibility;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }

          *:focus-visible {
            outline: 2px solid ${theme === "dark" ? "#3b82f6" : "#2563eb"};
            outline-offset: 2px;
            border-radius: 4px;
          }

          button:disabled {
            cursor: not-allowed !important;
            opacity: 0.6 !important;
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          /* Responsive improvements */
          @media (max-width: 768px) {
            button {
              min-height: 44px !important;
              min-width: 44px !important;
            }
          }

          /* High contrast mode support */
          @media (prefers-contrast: high) {
            button {
              border: 2px solid currentColor !important;
            }
          }

          /* Print styles */
          @media print {
            .modal {
              display: none !important;
            }
          }
        `}
      </style>
      <AlertModal
        open={alert.open}
        title={alert.title}
        message={alert.message}
        variant={alert.variant}
        showCancel={alert.showCancel}
        onConfirm={alert.onConfirm}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />
    </div>
  );
};

export default Mo3tiAdminDocumentRequests;
